import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface PlayerProfile {
  id: string;
  parent_user_id: string;
  player_user_id: string;
  username: string;
  display_name: string | null;
  sport: string;
  position: string | null;
  skill_level: string | null;
  avatar_color: string | null;
  created_at: string;
}

export interface PlayerWithProgress extends PlayerProfile {
  xp: number;
  streak: number;
  level: number;
  completed_lessons: string[];
}

export function usePlayerProfiles() {
  const { user } = useAuth();
  const [players, setPlayers] = useState<PlayerWithProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [isParent, setIsParent] = useState(false);
  const [isPlayer, setIsPlayer] = useState(false);
  const [playerProfile, setPlayerProfile] = useState<PlayerProfile | null>(null);

  const fetchPlayers = useCallback(async () => {
    if (!user) {
      setPlayers([]);
      setIsParent(false);
      setIsPlayer(false);
      setPlayerProfile(null);
      setLoading(false);
      return;
    }

    // Check if this user is a player
    const { data: asPlayer } = await (supabase
      .from('player_profiles') as any)
      .select('*')
      .eq('player_user_id', user.id)
      .maybeSingle();

    if (asPlayer) {
      setIsPlayer(true);
      setPlayerProfile(asPlayer);
      setIsParent(false);
      setPlayers([]);
      setLoading(false);
      return;
    }

    // Check if this user has persona=parent in their profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('user_persona')
      .eq('user_id', user.id)
      .single();
    
    const isParentPersona = (profile as any)?.user_persona === 'parent';

    // Check if this user is a parent (has players or has parent persona)
    const { data: ownedPlayers } = await (supabase
      .from('player_profiles') as any)
      .select('*')
      .eq('parent_user_id', user.id);

    if ((ownedPlayers && ownedPlayers.length > 0) || isParentPersona) {
      setIsParent(true);
      setIsPlayer(false);
      setPlayerProfile(null);

      if (ownedPlayers && ownedPlayers.length > 0) {
        // Fetch progress for each player
        const playerIds = ownedPlayers.map((p: any) => p.player_user_id);
        const { data: progressData } = await supabase
          .from('user_progress')
          .select('user_id, xp, streak, level, completed_lessons')
          .in('user_id', playerIds);

        const progressMap = new Map(
          (progressData || []).map((p) => [p.user_id, p])
        );

        const withProgress: PlayerWithProgress[] = ownedPlayers.map((p: any) => {
          const prog = progressMap.get(p.player_user_id);
          return {
            ...p,
            xp: prog?.xp ?? 0,
            streak: prog?.streak ?? 0,
            level: prog?.level ?? 0,
            completed_lessons: prog?.completed_lessons ?? [],
          };
        });

        setPlayers(withProgress);
      } else {
        setPlayers([]);
      }
    } else {
      setIsParent(false);
      setPlayers([]);
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const createPlayer = useCallback(
    async (data: {
      username: string;
      display_name: string;
      pin: string;
      sport: string;
      position?: string;
    }) => {
      const { data: result, error } = await supabase.functions.invoke(
        'manage-players',
        {
          body: { action: 'create', ...data },
        }
      );

      if (error) throw new Error(error.message);
      if (result?.error) throw new Error(result.error);

      await fetchPlayers();
      return result;
    },
    [fetchPlayers]
  );

  const deletePlayer = useCallback(
    async (playerId: string) => {
      const { data: result, error } = await supabase.functions.invoke(
        'manage-players',
        {
          body: { action: 'delete', player_id: playerId },
        }
      );

      if (error) throw new Error(error.message);
      if (result?.error) throw new Error(result.error);

      await fetchPlayers();
    },
    [fetchPlayers]
  );

  return {
    players,
    loading,
    isParent,
    isPlayer,
    playerProfile,
    createPlayer,
    deletePlayer,
    refetch: fetchPlayers,
  };
}
